import { useQuery } from "@tanstack/react-query";
import cx from "classnames";
import { useState } from "react";

import {
  useLegacyReferAFriend,
  useReferralRewardsFeature,
} from "@/modules/referralRewards";
import { get } from "api";
import { Button } from "atoms/button/Button";
import { Icon } from "atoms/icon/Icon";
import Rewards from "atoms/icon/icons/rewards_nav.svg";
import { RepeatMDFooter } from "atoms/repeatmd-footer/RepeatMDFooter";
import { useClipboard } from "hooks";

import { formatPlatformNameForReferral, formatTitleForReferral } from "./utils";

export type ReferAFriendProps = {
  buttonOnly?: boolean;
  withRepeatMDFooter?: boolean;
  showCopyLink?: boolean;
  buttonLabel?: string;
};

export const ReferAFriend = ({
  buttonOnly,
  withRepeatMDFooter,
  showCopyLink,
  buttonLabel,
}: ReferAFriendProps) => {
  const { showLegacyReferral } = useReferralRewardsFeature();
  const [referralUrl, setReferralUrl] = useState<string>("");

  const { copyToClipboard, isLoading: isCopyingLink } = useClipboard();

  const { data: platform } = useQuery({
    queryKey: ["/platform/current"],
    queryFn: () => get("/platform/current"),
  });

  const rewardsEnabled = !!platform?.platformSettings?.rewardsEnabled;

  const { data: referrableRewards } = useQuery({
    queryKey: ["/rewards/referrable"],
    queryFn: () => get("/rewards/referrable"),
    enabled: !!rewardsEnabled,
  });

  const referrableRewardTitle = formatTitleForReferral(
    referrableRewards?.[0]?.title
  );

  const formattedPlatformName = formatPlatformNameForReferral(platform?.name);

  const displayRewardTitle = referrableRewardTitle || "";

  const { shareLegacyReferral, handleCopyLink } = useLegacyReferAFriend({
    rewardsEnabled,
    platform: platform!,
    title: referrableRewardTitle || "",
    formattedPlatformName: formattedPlatformName || "",
    copyToClipboard: (text: string) => {
      copyToClipboard(text);
    },
    isCopyingLink,
    referralUrl,
    setReferralUrl,
  });

  const handleClick = () => {
    shareLegacyReferral();
  };

  if (!showLegacyReferral) {
    return null;
  }

  if (buttonOnly) {
    return (
      <>
        <Button fullWidth onClick={handleClick}>
          {buttonLabel || "Refer a friend"}
        </Button>
        {showCopyLink && (
          <div className="mt-1 w-full">
            <Button
              style="secondary"
              fullWidth
              onClick={handleCopyLink}
              disabled={isCopyingLink}
            >
              Copy link
            </Button>
          </div>
        )}
      </>
    );
  }

  return (
    <div className={cx(rewardsEnabled && !displayRewardTitle && "hidden")}>
      <div className="flex flex-col items-center bg-brand-color px-4 py-6">
        <Icon color="white" svg={Rewards} size="xsmall" />
        <div className="pb-4 pt-4 font-sans text-sub1 text-white">
          REFER A FRIEND
        </div>
        <div className="px-8 pb-6 text-center font-serif text-h2 text-white">
          {rewardsEnabled
            ? `Send a friend ${displayRewardTitle}!`
            : `Invite a friend to ${formattedPlatformName}`}
        </div>
        <Button style="outline" fullWidth onClick={handleClick}>
          {rewardsEnabled ? "Send to a friend" : "Refer a friend"}
        </Button>
      </div>
      {withRepeatMDFooter && <RepeatMDFooter />}
    </div>
  );
};
